// ============================================================================
// AttachmentsField — ajouter, voir et retirer les pieces jointes
// ============================================================================
// Une zone de depot (clic ou glisser-deposer) qui accepte PLUSIEURS fichiers a
// la fois, la liste des fichiers joints (vignette ou icone, nom, genre et
// taille, bouton « Retirer ») et, pour chaque fichier refuse, sa raison. Le
// tri vit dans piecesJointes.ts ; l'etat vit dans l'editeur, qui le donne aussi
// a l'apercu. Les fichiers partent avec la publication ; l'avis dit comment ils
// paraitront au fil.

import { useState } from 'react';
import type { TFunction } from 'i18next';
import { FilePlus2, Info, X } from 'lucide-react';
import { AttachmentTile } from './AttachmentParts';
import { attachmentMeta } from './attachmentLabels';
import {
  ATTACHMENT_ACCEPT, ATTACHMENT_MAX_FILES, type Attachment, type Refusal,
} from './piecesJointes';

export const ATTACHMENTS_INPUT_ID = 'editor-files-input';

function refusalText(r: Refusal, t: TFunction): string {
  if (r.reason === 'type') return t('gest:accueil.editor.files.refusedType', { name: r.name });
  if (r.reason === 'size') return t('gest:accueil.editor.files.refusedSize', { name: r.name, max: r.maxMb });
  if (r.reason === 'duplicate') return t('gest:accueil.editor.files.refusedDuplicate', { name: r.name });
  return t('gest:accueil.editor.files.refusedCount', { name: r.name, max: ATTACHMENT_MAX_FILES });
}

export function AttachmentsField({
  attachments,
  refused,
  onAdd,
  onRemove,
  locale,
  t,
}: {
  attachments: Attachment[];
  /** Refus du dernier ajout (vide quand tout est passe). */
  refused: Refusal[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
  locale: string;
  t: TFunction;
}) {
  const [dragging, setDragging] = useState(false);
  const full = attachments.length >= ATTACHMENT_MAX_FILES;

  return (
    <div>
      <input
        id={ATTACHMENTS_INPUT_ID}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        disabled={full}
        className="sr-only"
        aria-label={t('gest:accueil.editor.files.inputAria')}
        onChange={(e) => {
          onAdd(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      <label
        htmlFor={ATTACHMENTS_INPUT_ID}
        data-testid="files-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          if (!full) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!full) onAdd(Array.from(e.dataTransfer.files));
        }}
        className={`flex touch-manipulation flex-col items-center gap-1.5 rounded-xl border border-dashed px-4 py-6 text-center transition-colors ${
          full
            ? 'cursor-not-allowed border-slate-200 opacity-60 dark:border-slate-700'
            : dragging
              ? 'cursor-pointer border-brand-400 bg-slate-50 dark:bg-slate-700/40'
              : 'cursor-pointer border-slate-300 hover:border-brand-400 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/40'
        }`}
      >
        <FilePlus2 size={24} aria-hidden className="text-slate-400 dark:text-slate-500" />
        <span className="text-field font-medium text-slate-700 dark:text-slate-200">
          {full ? t('gest:accueil.editor.files.full', { max: ATTACHMENT_MAX_FILES }) : t('gest:accueil.editor.files.choose')}
        </span>
        <span className="text-meta text-slate-400 dark:text-slate-500">
          {t('gest:accueil.editor.files.formats', { max: ATTACHMENT_MAX_FILES })}
        </span>
      </label>

      {refused.length > 0 && (
        <ul role="alert" className="mt-2 space-y-1 text-field text-red-600 dark:text-red-400">
          {refused.map((r) => (
            <li key={`${r.name}-${r.reason}`}>{refusalText(r, t)}</li>
          ))}
        </ul>
      )}

      {attachments.length > 0 && (
        <ul aria-label={t('gest:accueil.editor.files.listAria')} className="mt-3 divide-y divide-slate-100 dark:divide-slate-700/60">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-3 py-2">
              <AttachmentTile attachment={a} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-field font-medium text-slate-700 dark:text-slate-200">{a.name}</p>
                <p className="text-meta text-slate-400 dark:text-slate-500">{attachmentMeta(a, locale, t)}</p>
              </div>
              <button
                type="button"
                onClick={() => onRemove(a.id)}
                aria-label={t('gest:accueil.editor.files.removeAria', { name: a.name })}
                className="flex h-9 w-9 shrink-0 touch-manipulation items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                <X size={16} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 flex items-start gap-2 text-meta text-slate-500 dark:text-slate-400">
        <Info size={14} aria-hidden className="mt-0.5 shrink-0 text-blue-500" />
        <span>{t('gest:accueil.editor.files.notice')}</span>
      </p>
    </div>
  );
}
